import dayjs from 'dayjs';
import {isActiveWorkspaceUser} from '@utils/active-workspace-user';
import {
  buildOccurrenceGroups,
  getParticipationStats,
  getSubmissionCycleId,
  getManagedSubmissionUserId,
  getCheckinSubmissionStatus,
  checkinSubmissionStatusLabel,
  isSubmissionSubmitted,
  ManagedSubmission,
} from '@pages/dashboard/checkins/team-management/team-management.utils';
import {
  goalIsEnded,
  goalPerformance,
  goalProgress,
  goalsFromResponse,
} from '@pages/dashboard/goals/my-objective/my-goals-v2.utils';
import {
  DashboardMember,
  FeedbackSignal,
  GoalHealth,
  GoalSignal,
  ManagerDashboardModel,
  ManagerSignal,
  ProgramSignal,
  TeamMapRow,
} from './manager-dashboard.types';

export const normalizeList = (response: any): any[] => {
  if (Array.isArray(response)) return response;
  for (const key of ['data', 'results', 'users', 'groups']) {
    if (Array.isArray(response?.[key])) return response[key];
  }
  return [];
};

export const referenceId = (value: any): string => {
  if (typeof value === 'string') return value;
  return value?.id || value?._id || '';
};

export const managerId = (user: any): string => referenceId(user?.reviewer);

export const personName = (user: any): string => {
  const name = `${user?.firstName || ''} ${user?.lastName || ''}`.trim();
  return name || user?.name || user?.email || 'Team member';
};

export const activeWorkspaceUsers = (response: any): any[] =>
  normalizeList(response).filter(isActiveWorkspaceUser);

export const toDashboardMember = (user: any): DashboardMember => {
  const timezone = user?.customTimezone || user?.timezone;
  return {
    id: referenceId(user),
    name: personName(user),
    firstName: user?.firstName || personName(user).split(' ')[0],
    email: user?.email,
    jobTitle: user?.jobTitle,
    avatar: user?.avatar?.url || user?.avatar,
    timezone:
      typeof timezone === 'string'
        ? timezone
        : timezone?.value || timezone?.timezone || timezone?.name,
    raw: user,
  };
};

const groupReferenceIds = (value: any): string[] => {
  if (typeof value === 'string') return [value];
  if (!value) return [];
  return [
    referenceId(value),
    referenceId(value?.group),
    referenceId(value?.user),
    referenceId(value?.member),
  ].filter(Boolean);
};

export const userBelongsToGroup = (user: any, group: any): boolean => {
  const groupId = referenceId(group);
  const userId = referenceId(user);
  if (!groupId || !userId) return false;
  const membershipMatch = [
    ...(user?.groups || []),
    ...(user?.group || []),
  ].some((membership) => groupReferenceIds(membership).includes(groupId));
  const listedMember = (group?.members || []).some((member: any) =>
    groupReferenceIds(member).includes(userId),
  );
  return membershipMatch || listedMember;
};

export const managerCanUseGroup = (
  group: any,
  manager: any,
  isAdmin: boolean,
) => {
  if (isAdmin) return true;
  const id = referenceId(manager);
  return (
    userBelongsToGroup(manager, group) ||
    [group?.lead, group?.manager, group?.owner, group?.reviewer].some(
      (value) => referenceId(value) === id,
    )
  );
};

export const membersForScope = ({
  users,
  groups,
  currentUser,
  scope,
}: {
  users: any[];
  groups: any[];
  currentUser: any;
  scope: string;
}): DashboardMember[] => {
  const currentUserId = referenceId(currentUser);
  let selected = users.filter((user) => managerId(user) === currentUserId);

  if (scope === 'workspace') {
    selected = users.filter((user) => referenceId(user) !== currentUserId);
  } else if (scope.startsWith('group:')) {
    const groupId = scope.slice('group:'.length);
    const group = groups.find((item) => referenceId(item) === groupId);
    selected = group
      ? users.filter((user) => userBelongsToGroup(user, group))
      : [];
  }

  return selected
    .map(toDashboardMember)
    .sort((a, b) => a.name.localeCompare(b.name));
};

const goalOwnerId = (goal: any) =>
  referenceId(goal?.assignee || goal?.owner || goal?.user);

export const goalHealth = (goal: any): GoalHealth => {
  if (goalIsEnded(goal)) return 'completed';
  const performance = goalPerformance(goal);
  if (performance === 'behind' || performance === 'at_risk') return performance;
  if (performance === 'on_track') return 'on_track';
  return 'no_status';
};

export const buildGoalSignals = (
  response: any,
  members: DashboardMember[],
): GoalSignal[] => {
  const memberMap = new Map(members.map((member) => [member.id, member]));
  return goalsFromResponse(response)
    .map((goal) => {
      const member = memberMap.get(goalOwnerId(goal));
      if (!member) return null;
      return {
        id: referenceId(goal),
        title: goal?.name || goal?.title || 'Untitled goal',
        memberId: member.id,
        memberName: member.name,
        status: goalHealth(goal),
        progress: goalProgress(goal),
        goal,
      } as GoalSignal;
    })
    .filter((goal): goal is GoalSignal => Boolean(goal?.id));
};

const feedbackRowUserId = (row: any) =>
  referenceId(row?.user || row?.assignee || row?.recipient || row);

export const buildFeedbackSignals = ({
  response,
  requests,
  publicPraise,
  members,
  periodStart,
}: {
  response: any;
  requests: any;
  publicPraise: any;
  members: DashboardMember[];
  periodStart: string;
}): FeedbackSignal[] => {
  const rows = normalizeList(response?.users || response);
  const requestsList = normalizeList(requests);
  const praiseList = normalizeList(publicPraise);
  const today = dayjs();

  return members.map((member) => {
    const row =
      rows.find((item) => feedbackRowUserId(item) === member.id) || {};
    const feedback = row?.feedback || row;
    const received = Number(
      feedback?.received ?? feedback?.given ?? feedback?.totalFeedback ?? 0,
    );
    const praiseFromReport = Number(feedback?.praise ?? feedback?.public ?? 0);
    const praise =
      praiseList.filter((item) => feedbackRowUserId(item) === member.id)
        .length || praiseFromReport;
    const pendingRequests = requestsList.filter((request) => {
      const recipientId = referenceId(
        request?.user || request?.recipient || request?.reviewee,
      );
      const status = String(request?.status || '').toLowerCase();
      return (
        recipientId === member.id && !['completed', 'done'].includes(status)
      );
    }).length;
    const lastFeedbackAt =
      feedback?.lastFeedbackAt || row?.lastFeedbackAt || row?.updatedAt;
    const gapDays = Math.max(
      0,
      today.diff(dayjs(lastFeedbackAt || periodStart), 'day'),
    );

    return {
      memberId: member.id,
      memberName: member.name,
      received,
      praise,
      substantive: Math.max(0, received - praise),
      pendingRequests,
      gapDays,
    };
  });
};

export const buildProgramSignals = ({
  submissionsResponse,
  cyclesResponse,
  memberIds,
  workspaceTimezone,
  allowUnscopedAnonymous = false,
}: {
  submissionsResponse: any;
  cyclesResponse: any;
  memberIds: string[];
  workspaceTimezone?: string;
  allowUnscopedAnonymous?: boolean;
}): ProgramSignal[] => {
  const memberSet = new Set(memberIds);
  const allSubmissions = normalizeList(
    submissionsResponse,
  ) as ManagedSubmission[];
  const cycles = normalizeList(cyclesResponse);
  const cycleMap = new Map<string, any>();
  cycles.forEach((cycle) => cycleMap.set(referenceId(cycle), cycle));
  allSubmissions.forEach((submission) => {
    const cycle =
      typeof submission.cycle === 'object' ? submission.cycle : null;
    if (cycle && !cycleMap.has(referenceId(cycle))) {
      cycleMap.set(referenceId(cycle), cycle);
    }
  });

  const visibleRows = allSubmissions.filter((submission) => {
    const userId = getManagedSubmissionUserId(submission);
    const cycleId = getSubmissionCycleId(submission);
    const cycle =
      cycleMap.get(cycleId || '') ||
      (typeof submission.cycle === 'object' ? submission.cycle : {});
    if (cycle?.visibility === 'anonymous') {
      return userId ? memberSet.has(userId) : allowUnscopedAnonymous;
    }
    return Boolean(userId && memberSet.has(userId));
  });

  const grouped = new Map<string, ManagedSubmission[]>();
  visibleRows.forEach((submission) => {
    const cycleId = getSubmissionCycleId(submission);
    if (!cycleId) return;
    grouped.set(cycleId, [...(grouped.get(cycleId) || []), submission]);
  });

  return Array.from(grouped.entries())
    .map(([cycleId, submissions]) => {
      const cycle =
        cycleMap.get(cycleId) ||
        (typeof submissions[0]?.cycle === 'object' ? submissions[0].cycle : {});
      const anonymous = cycle?.visibility === 'anonymous';
      const occurrenceGroups = buildOccurrenceGroups(
        submissions,
        cycle,
        workspaceTimezone,
      );
      const occurrences = occurrenceGroups.map((occurrence) => {
        const stats = getParticipationStats(occurrence.submissions);
        return {
          key: occurrence.key,
          label: occurrence.label,
          submitted: stats.submittedCount,
          pending: stats.pendingCount,
          total: stats.totalCount,
          percentage: stats.percentage,
          submissions: occurrence.submissions,
        };
      });
      return {
        id: cycleId,
        name:
          cycle?.cycleName ||
          submissions[0]?.cycleName ||
          'Unnamed check-in cycle',
        status: cycle?.status || 'active',
        visibility: cycle?.visibility || 'private',
        anonymous,
        occurrences,
        latest: occurrences[0],
        cycle,
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name));
};

const goalRank: Record<GoalHealth, number> = {
  behind: 5,
  at_risk: 4,
  no_status: 3,
  on_track: 2,
  completed: 1,
};

export const sortSignals = (items: ManagerSignal[]) =>
  [...items].sort(
    (a, b) => b.severity - a.severity || a.title.localeCompare(b.title),
  );

const readableList = (items: string[]) => {
  if (items.length < 2) return items[0] || '';
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
};

const countLabel = (count: number, singular: string, plural = `${singular}s`) =>
  `${count} ${count === 1 ? singular : plural}`;

export const buildPersonSummary = ({
  member,
  goals,
  feedback,
  programs,
  include = {goals: true, feedback: true, programs: true},
}: {
  member: DashboardMember;
  goals: GoalSignal[];
  feedback?: FeedbackSignal;
  programs: ProgramSignal[];
  include?: {goals: boolean; feedback: boolean; programs: boolean};
}) => {
  const sentences: string[] = [];
  const name = member.firstName || member.name;

  if (include.goals) {
    if (!goals.length) {
      sentences.push(`${name} has no goals in this period.`);
    } else {
      const statuses: Array<{status: GoalHealth; label: string}> = [
        {status: 'on_track', label: 'on track'},
        {status: 'at_risk', label: 'at risk'},
        {status: 'behind', label: 'behind'},
        {status: 'completed', label: 'ended'},
        {status: 'no_status', label: 'with no status'},
      ];
      const statusSummary = statuses
        .map(({status, label}) => ({
          count: goals.filter((goal) => goal.status === status).length,
          label,
        }))
        .filter(({count}) => count > 0)
        .map(({count, label}) => `${count} ${label}`);
      sentences.push(
        `${name} has ${countLabel(
          goals.length,
          'goal',
        )} in this period: ${readableList(statusSummary)}.`,
      );

      const priorityGoal = [...goals]
        .filter((goal) => ['behind', 'at_risk'].includes(goal.status))
        .sort(
          (a, b) =>
            (a.status === 'behind' ? 0 : 1) - (b.status === 'behind' ? 0 : 1) ||
            a.progress - b.progress,
        )[0];
      if (priorityGoal) {
        sentences.push(
          `${priorityGoal.title} is the clearest priority at ${priorityGoal.progress}% progress.`,
        );
      }
    }
  }

  if (include.feedback) {
    if (!feedback || (!feedback.substantive && !feedback.praise)) {
      sentences.push(
        `No substantive feedback or praise was recorded for ${name} in this period.`,
      );
    } else {
      const feedbackActivity = [
        feedback.substantive
          ? countLabel(
              feedback.substantive,
              'substantive feedback item',
              'substantive feedback items',
            )
          : '',
        feedback.praise ? countLabel(feedback.praise, 'praise post') : '',
      ].filter(Boolean);
      sentences.push(
        `${name} received ${readableList(feedbackActivity)} in this period.`,
      );
    }
    if (feedback?.pendingRequests) {
      sentences.push(
        `${countLabel(
          feedback.pendingRequests,
          'feedback request is',
          'feedback requests are',
        )} awaiting a response.`,
      );
    }
  }

  if (include.programs) {
    const submissions = programs
      .filter((program) => !program.anonymous)
      .flatMap((program) => program.latest?.submissions || [])
      .filter(
        (submission) =>
          getManagedSubmissionUserId(submission) === member.id &&
          !submission.isExcused,
      );
    if (!submissions.length) {
      sentences.push(
        `No identifiable check-in activity is available for ${name}.`,
      );
    } else {
      const submitted = submissions.filter(isSubmissionSubmitted).length;
      const overdue = submissions.filter(
        (submission) =>
          getCheckinSubmissionStatus(submission).kind === 'overdue',
      ).length;
      const outstanding = submissions.length - submitted;
      sentences.push(
        `${name} submitted ${submitted} of ${
          submissions.length
        } latest check-ins; ${
          outstanding
            ? `${countLabel(
                outstanding,
                'submission remains',
                'submissions remain',
              )} outstanding${
                overdue
                  ? `, including ${overdue} overdue check-in${
                      overdue === 1 ? '' : 's'
                    }`
                  : ', with none overdue'
              }`
            : 'all are complete'
        }.`,
      );
    }
  }

  return (
    sentences.join(' ') || 'No reporting data is available for this person yet.'
  );
};

export const buildManagerDashboardModel = ({
  members,
  goals,
  feedback,
  programs,
}: {
  members: DashboardMember[];
  goals: GoalSignal[];
  feedback: FeedbackSignal[];
  programs: ProgramSignal[];
}): ManagerDashboardModel => {
  const signals: ManagerSignal[] = [];

  goals.forEach((goal) => {
    if (goal.status === 'behind') {
      signals.push({
        id: `goal-behind-${goal.id}`,
        kind: 'goal-behind',
        memberId: goal.memberId,
        memberName: goal.memberName,
        title: goal.memberName,
        detail: goal.title,
        label: `${goal.progress}% · Behind`,
        severity: 4000 + (100 - goal.progress),
      });
    } else if (goal.status === 'at_risk') {
      signals.push({
        id: `goal-risk-${goal.id}`,
        kind: 'goal-risk',
        memberId: goal.memberId,
        memberName: goal.memberName,
        title: goal.memberName,
        detail: goal.title,
        label: `${goal.progress}% · At risk`,
        severity: 2000 + (100 - goal.progress),
      });
    }
  });

  programs.forEach((program) => {
    if (program.anonymous) return;
    (program.latest?.submissions || []).forEach((submission: any) => {
      const status = getCheckinSubmissionStatus(submission);
      const days = status.daysOverdue;
      const memberId = getManagedSubmissionUserId(submission);
      const member = members.find((item) => item.id === memberId);
      if (status.kind !== 'overdue' || !member) return;
      signals.push({
        id: `program-${program.id}-${member.id}`,
        kind: 'program-overdue',
        memberId: member.id,
        memberName: member.name,
        title: member.name,
        detail: program.name,
        label: checkinSubmissionStatusLabel(status),
        severity: 3000 + days,
      });
    });
  });

  feedback.forEach((signal) => {
    if (signal.pendingRequests < 1) return;
    signals.push({
      id: `feedback-request-${signal.memberId}`,
      kind: 'feedback-requested',
      memberId: signal.memberId,
      memberName: signal.memberName,
      title: signal.memberName,
      detail: 'Awaiting response',
      label: `${signal.pendingRequests} pending`,
      severity: 1000 + signal.pendingRequests,
    });
  });

  const sortedSignals = sortSignals(signals);
  const teamMap: TeamMapRow[] = members.map((member) => {
    const memberSignals = sortedSignals.filter(
      (item) => item.memberId === member.id,
    );
    const memberGoals = goals.filter((goal) => goal.memberId === member.id);
    const worstGoal = memberGoals
      .map((goal) => goal.status)
      .sort((a, b) => goalRank[b] - goalRank[a])[0];
    const memberFeedback = feedback.find(
      (signal) => signal.memberId === member.id,
    );
    const programRows = programs
      .filter((program) => !program.anonymous)
      .flatMap((program) => program.latest?.submissions || [])
      .filter(
        (submission) => getManagedSubmissionUserId(submission) === member.id,
      );
    const overduePrograms = programRows.filter(
      (submission) => getCheckinSubmissionStatus(submission).kind === 'overdue',
    ).length;
    const submittedPrograms = programRows.filter(isSubmissionSubmitted).length;
    const issueScore = memberSignals[0]?.severity || 0;
    return {
      member,
      signals: memberSignals,
      goals: memberGoals,
      worstGoal,
      feedback: memberFeedback,
      submittedPrograms,
      overduePrograms,
      issueScore,
    };
  });
  teamMap.sort(
    (a, b) =>
      b.issueScore - a.issueScore || a.member.name.localeCompare(b.member.name),
  );

  return {
    members,
    goals,
    feedback,
    programs,
    teamMap,
  };
};
